import React, { useCallback, useEffect, useRef, useState } from "react";
import { WallType, VoidType } from "../../types/floorplannerTypes";
import DraggableObject from "./DraggableObject";
import { BoundingBox, SelectableSymbol } from "./SelectableSymbol";
import { FloorplannerStoreContext } from '../../store/floorplannerStore';
import AttachableSymbol from "./AttachableSymbol";
import { Line } from "@react-three/drei";
import * as THREE from "three";
import { editorStore } from "../../store/editorStore";
import { observer } from "mobx-react-lite";
import { set, transaction } from "mobx";
import { useThree } from "@react-three/fiber";
import { SimpleLine } from "./SimpleLine";
import { AttachmentMeasurementLines } from "./AttachmentMeasurementLines";
import {
  getWallAttachmentRenderOrder,
  getWallAttachmentZOffset,
  usePlanarWallAttachmentLayer,
} from "./wallAttachmentRenderLayer";
import { CANVAS_ACCENT_COLOR } from "../../constants/brandColors";

interface VoidProps {
  void: VoidType;
  attachedToWall?: WallType;
  onDragStart: (startPosition: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

const Void: React.FC<VoidProps> = observer(({
  void: voidSymbol,
  attachedToWall,
  onDragStart,
  onDrag,
  onDragEnd,
}) => {
  const { gl, camera } = useThree();
  const floorplannerStore = React.useContext(FloorplannerStoreContext);
  const voidLength = voidSymbol.voidLength || floorplannerStore.windowLength;
  const voidWidth = voidSymbol.voidWidth || floorplannerStore.windowWidth;
  const lineWeight = voidSymbol.lineWeight || floorplannerStore.windowLineWeight;
  const color = voidSymbol.lineColor || floorplannerStore.lineColor;
  const wallLineWeightPixels = attachedToWall?.lineWeight || floorplannerStore.wallLineWeight;
  const wallLineWeight = floorplannerStore.convertLineWeightToWorld(wallLineWeightPixels);
  const wallLineColor = attachedToWall?.lineColor || floorplannerStore.lineColor;
  const wallWidth = attachedToWall?.wallWidth || floorplannerStore.wallWidth;
  const [isHovered, setIsHovered] = useState(false);
  const groupRef = useRef<THREE.Group>(null);
  const wallAttachmentLayerRef = useRef<THREE.Group>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const noopRaycast = () => null;
  const isAttachedToWall = Boolean(attachedToWall);
  const wallAttachmentRenderOrder = getWallAttachmentRenderOrder(voidSymbol.zIndex);

  usePlanarWallAttachmentLayer(
    wallAttachmentLayerRef,
    isAttachedToWall,
    wallAttachmentRenderOrder,
  );

  const setWallAttachmentLayerRef = useCallback((node: THREE.Group | null) => {
    groupRef.current = node;
    wallAttachmentLayerRef.current = node;
  }, []);

  const calculateBoundingBox = (): BoundingBox => {
    let leftMost = -voidLength / 2;
    let rightMost = voidLength / 2;
    if (attachedToWall && wallWidth >= 0.2) {
      leftMost = Math.min(leftMost, -wallWidth / 2 - wallLineWeight);
      rightMost = Math.max(rightMost, wallWidth / 2 + wallLineWeight);
    }
    return {
      topLeft: [leftMost, voidWidth / 2],
      topRight: [rightMost, voidWidth / 2],
      bottomLeft: [leftMost, -voidWidth / 2],
      bottomRight: [rightMost, -voidWidth / 2],
      width: rightMost - leftMost,
      height: voidWidth,
      depth: 0.1,
    };
  };

  const customRotation = voidSymbol.rotation ? voidSymbol.rotation : 0;

  useEffect(() => {
    if (groupRef.current) {
      editorStore.updateGroupRef(voidSymbol.id, groupRef.current);
    }
  }, [voidSymbol.id, groupRef.current]);

  useEffect(() => {
  }, [voidSymbol.selected]);

  return (
    <group
      position={[0, 0, getWallAttachmentZOffset(isAttachedToWall, voidSymbol.zIndex)]}
    >
      {/* Attachment measurement lines - show distance from wall ends while dragging */}
      {attachedToWall && (
        <AttachmentMeasurementLines
          symbol={voidSymbol}
          attachedWall={attachedToWall}
          isDragging={isDragging}
        />
      )}
      <DraggableObject
        position={[voidSymbol.position.x, voidSymbol.position.y]}
        onDragStart={() => {
          setIsDragging(true);
          onDragStart
        }}
        onDragEnd={() => {
          onDragEnd
          setIsDragging(false);
        }}
        onDrag={(newPosition) => {
          onDrag(newPosition);
        }}
        selectable={true}
        attachmentId={voidSymbol.id}
        attachmentType="voidAttachments"
        symbol={voidSymbol}
      >
        <AttachableSymbol
          attachmentId={voidSymbol.id}
          attachmentType="voidAttachments"
          onAttachment={() => { }}
          store={floorplannerStore}
        >
          <SelectableSymbol
            ref={groupRef}
            handleSize={floorplannerStore.symbolHandleSize}
            calculateBoundingBox={calculateBoundingBox}
            onResizeStart={() => setIsResizing(true)}
            onResize={(newWidth, newHeight, boundingBox, handle) => {
              const widthDiff = (newWidth - voidLength) / 2;
              const heightDiff = 0;
              transaction(() => {
                floorplannerStore.updateSymbolProperty(voidSymbol.id, "voidLength", newWidth);
                let position = [voidSymbol.position.x, voidSymbol.position.y];
                if (handle === "topLeft") {
                  position = [
                    voidSymbol.position.x - widthDiff * Math.cos(voidSymbol.rotation || 0) + heightDiff * Math.sin(voidSymbol.rotation || 0),
                    voidSymbol.position.y - widthDiff * Math.sin(voidSymbol.rotation || 0) + heightDiff * Math.cos(voidSymbol.rotation || 0)
                  ];
                } else if (handle === "topRight") {
                  position = [
                    voidSymbol.position.x + widthDiff * Math.cos(voidSymbol.rotation || 0) + heightDiff * Math.sin(voidSymbol.rotation || 0),
                    voidSymbol.position.y + widthDiff * Math.sin(voidSymbol.rotation || 0) + heightDiff * Math.cos(voidSymbol.rotation || 0)
                  ];
                } else if (handle === "bottomLeft") {
                  position = [
                    voidSymbol.position.x - widthDiff * Math.cos(voidSymbol.rotation || 0) - heightDiff * Math.sin(voidSymbol.rotation || 0),
                    voidSymbol.position.y - widthDiff * Math.sin(voidSymbol.rotation || 0) - heightDiff * Math.cos(voidSymbol.rotation || 0)
                  ];
                } else if (handle === "bottomRight") {
                  position = [
                    voidSymbol.position.x + widthDiff * Math.cos(voidSymbol.rotation || 0) - heightDiff * Math.sin(voidSymbol.rotation || 0),
                    voidSymbol.position.y + widthDiff * Math.sin(voidSymbol.rotation || 0) - heightDiff * Math.cos(voidSymbol.rotation || 0)
                  ];
                }
                if (attachedToWall) {
                  floorplannerStore.detachSymbolFromWall(voidSymbol.id);
                  floorplannerStore.updateSymbolProperty(voidSymbol.id, "position", position);
                  floorplannerStore.attachSymbolToWall(voidSymbol.id, attachedToWall.id, [position[0], position[1]]);
                } else {
                  floorplannerStore.updateSymbolProperty(voidSymbol.id, "position", position);
                }
              });
            }}
            onResizeEnd={() => setIsResizing(false)}
            rotation={customRotation}
            symbol={voidSymbol}
            onPointerOver={() => setIsHovered(true)}
            onPointerOut={() => setIsHovered(false)}
            isDragging={isDragging}
          >
            <group
              ref={setWallAttachmentLayerRef}
              rotation={[0, 0, customRotation]}
              renderOrder={isAttachedToWall ? wallAttachmentRenderOrder : undefined}
            >
              {/* Invisible box to catch events on any unfilled areas */}
              <mesh
                position={[0, 0, -0.03]}
                rotation={[0, 0, 0]}
                onPointerEnter={() => {
                  if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
                }}
                onPointerLeave={() => {
                  if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
                }}
              >
                <boxGeometry args={[voidLength, voidWidth, 0.001]} />
                <meshBasicMaterial
                  transparent
                  opacity={0.0}
                />
              </mesh>

              {/* If void is attached to a wall */}
              {attachedToWall && (
                <>
                  {/* close the endpoints by drawing a line across the opening with the wall properties, but only if wallWidth is not 0.1 */}
                  {wallWidth >= 0.1 && (
                    <>
                      <SimpleLine
                        points={[
                          [-voidLength / 2, -wallWidth / 2, 0],
                          [-voidLength / 2, wallWidth / 2, 0]
                        ]}
                        color={wallLineColor}
                        lineWidth={wallLineWeightPixels}
                      />
                      <SimpleLine
                        points={[
                          [voidLength / 2, -wallWidth / 2, 0],
                          [voidLength / 2, wallWidth / 2, 0]
                        ]}
                        color={wallLineColor}
                        lineWidth={wallLineWeightPixels}
                      />
                    </>
                  )}
                  {/* No horizontal lines across the void - it should be completely empty */}
                </>
              )}

              {/* Void outline - only visible when unattached OR when selected */}
              {(!attachedToWall || voidSymbol.selected) && (
                <>
                  {/* Left outline */}
                  <Line
                    points={[
                      [-voidSymbol.voidLength / 2, voidSymbol.voidWidth / 2, 0],
                      [-voidSymbol.voidLength / 2, -voidSymbol.voidWidth / 2, 0]
                    ]}
                    color={CANVAS_ACCENT_COLOR}
                    lineWidth={lineWeight}
                    dashed={true}
                    dashSize={0.15}
                    gapSize={0.1}
                    raycast={noopRaycast}
                  />
                  {/* Right outline */}
                  <Line
                    points={[
                      [voidSymbol.voidLength / 2, voidSymbol.voidWidth / 2, 0],
                      [voidSymbol.voidLength / 2, -voidSymbol.voidWidth / 2, 0]
                    ]}
                    color={CANVAS_ACCENT_COLOR}
                    lineWidth={lineWeight}
                    dashed={true}
                    dashSize={0.15}
                    gapSize={0.1}
                    raycast={noopRaycast}
                  />
                  {/* Top outline */}
                  <Line
                    points={[
                      [-voidSymbol.voidLength / 2, voidSymbol.voidWidth / 2, 0],
                      [voidSymbol.voidLength / 2, voidSymbol.voidWidth / 2, 0]
                    ]}
                    color={CANVAS_ACCENT_COLOR}
                    lineWidth={lineWeight}
                    dashed={true}
                    dashSize={0.15}
                    gapSize={0.1}
                    raycast={noopRaycast}
                  />
                  {/* Bottom outline */}
                  <Line
                    points={[
                      [-voidSymbol.voidLength / 2, -voidSymbol.voidWidth / 2, 0],
                      [voidSymbol.voidLength / 2, -voidSymbol.voidWidth / 2, 0]
                    ]}
                    color={CANVAS_ACCENT_COLOR}
                    lineWidth={lineWeight}
                    dashed={true}
                    dashSize={0.15}
                    gapSize={0.1}
                    raycast={noopRaycast}
                  />
                </>
              )}
            </group>
          </SelectableSymbol>
        </AttachableSymbol>
      </DraggableObject>
    </group>
  );
});

export default Void;
